import { toast } from 'react-toastify';
import { css } from '@emotion/react';
import { Spinner } from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { spacing, colors } from '../styles';

// TypeScript interface for Network Information API
interface NetworkInformation extends EventTarget {
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g' | 'offline';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

// Timing constants
const TIMING = {
  OFFLINE_THRESHOLD_MS: 3000, // Only show "back online" toast if offline for 3+ seconds
  ONLINE_TOAST_DURATION_MS: 2000, // Auto-close "back online" toast after 2 seconds
  INITIAL_CHECK_DELAY_MS: 1000, // Delay before initial connectivity check
} as const;

// Export utility function to check if online
export const checkIsOnline = (): boolean => {
  // Check both navigator.onLine and connection state
  if (!navigator.onLine) return false;

  // Check connection type if available
  const nav = navigator as NavigatorWithConnection;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  if (connection?.effectiveType === 'offline') return false;

  return true;
};

// Module-level state to persist across event handlers
const offlineState = {
  offlineTime: null as number | null,
  wasOffline: false,
};

export default () => {
  const offlineId = 'offlineId';
  const onlineId = 'onlineId';

  const triggerOfflineToast = () => {
    // Don't show duplicate toasts
    if (toast.isActive(offlineId)) return;

    offlineState.offlineTime = Date.now();
    offlineState.wasOffline = true;

    toast(
      () => (
        <span
          css={css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <span>
            <Spinner color={colors.cloud} size="xs" />
          </span>
          <span
            css={css`
              flex: 1;
              margin-left: ${spacing.deci};
            `}
          >
            Connection lost. <br /> Please check your internet connection.
          </span>
        </span>
      ),
      {
        toastId: offlineId,
        autoClose: false,
      },
    );
  };

  const triggerOnlineToast = () => {
    // Dismiss offline toast first
    if (toast.isActive(offlineId)) {
      toast.dismiss(offlineId);
    }

    // Only show online toast if we were offline for more than threshold
    // This prevents annoying flashing toasts for brief disconnections
    const offlineDuration = offlineState.offlineTime ? Date.now() - offlineState.offlineTime : 0;
    if (!offlineState.wasOffline || offlineDuration < TIMING.OFFLINE_THRESHOLD_MS) {
      offlineState.wasOffline = false;
      offlineState.offlineTime = null;
      return;
    }

    toast(
      () => (
        <span
          css={css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <span>
            <CheckCircleIcon color={colors.leaf} />
          </span>
          <span
            css={css`
              flex: 1;
              margin-left: ${spacing.deci};
            `}
          >
            Back online
          </span>
        </span>
      ),
      {
        toastId: onlineId,
        autoClose: TIMING.ONLINE_TOAST_DURATION_MS,
      },
    );

    offlineState.wasOffline = false;
    offlineState.offlineTime = null;
  };

  const handleOffline = () => {
    if (!checkIsOnline()) {
      triggerOfflineToast();
    }
  };

  const handleOnline = () => {
    // Verify we're actually online before showing toast
    if (checkIsOnline()) {
      triggerOnlineToast();
    }
  };

  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  // Check initial state only if already offline (with delay to ensure DOM is ready)
  if (!checkIsOnline()) {
    setTimeout(() => {
      if (!checkIsOnline()) {
        triggerOfflineToast();
      }
    }, TIMING.INITIAL_CHECK_DELAY_MS);
  }

  // Return cleanup function to prevent memory leaks
  return () => {
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
  };
};
